import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify/${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        // Redirect to login after 3 seconds
        setTimeout(() => navigate('/login'), 3000);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };

    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link.');
    }
  }, [token, navigate]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: email.trim().toLowerCase() });
      setResendSuccess(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-tiffany-500 animate-spin" />
              </div>
              <h1 className="font-sans text-3xl text-white mb-4">VERIFYING...</h1>
              <p className="font-mono text-zinc-400">Please wait while we verify your email.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 border-2 border-tiffany-500 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-tiffany-500" />
              </div>
              <h1 className="font-sans text-3xl text-white mb-4">EMAIL VERIFIED</h1>
              <p className="font-mono text-zinc-400 mb-6">{message}</p>
              <p className="font-mono text-sm text-zinc-500 mb-8">Redirecting to login...</p>
              <Link to="/login">
                <Button className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none">
                  GO TO LOGIN
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 border-2 border-red-500 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-sans text-3xl text-white mb-4">VERIFICATION FAILED</h1>
              <p className="font-mono text-zinc-400 mb-4">{message}</p>
              
              {/* Resend verification section */}
              <div className="border border-zinc-800 p-6 mb-6 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-tiffany-500" />
                  <p className="font-mono text-sm text-white">NEED A NEW LINK?</p>
                </div>
                
                {resendSuccess ? (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-tiffany-500 mx-auto mb-2" />
                    <p className="font-mono text-sm text-tiffany-500">
                      New verification email sent!
                    </p>
                    <p className="font-mono text-xs text-zinc-500 mt-1">
                      Check your inbox and spam folder
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleResendVerification} className="space-y-3">
                    <p className="font-mono text-xs text-zinc-500 mb-3">
                      Enter your email to receive a new verification link
                    </p>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 focus:outline-none"
                      data-testid="resend-email-input"
                    />
                    <Button 
                      type="submit"
                      disabled={resending}
                      className="w-full h-10 bg-zinc-800 text-tiffany-500 hover:bg-zinc-700 font-mono text-sm tracking-wider rounded-none border border-zinc-700"
                      data-testid="resend-verification-btn"
                    >
                      {resending ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> SENDING...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" /> SEND NEW LINK</>
                      )}
                    </Button>
                  </form>
                )}
              </div>

              <div className="space-y-4">
                <Link to="/login">
                  <Button className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none">
                    GO TO LOGIN
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" className="w-full h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-sans tracking-widest rounded-none">
                    CREATE NEW ACCOUNT
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
